import {
  ActionLink,
  LearningShell,
  ModuleIcon,
  RecentTrainingRecordsPanel,
  ToneBadge,
} from '../features/learning-space/components';
import { useSearchParams } from 'react-router-dom';
import {
  buildLearningRouteScope,
  buildScopedLearningHref,
  parseTrainingLaunchIntent,
  type TcfModuleId,
  type UserLearningContext,
} from '@examflow/learning-space';
import { useTrainingEntryController } from '../features/learning-space/useTrainingEntryController';
import { useLearningViewer } from '../features/learning-space/useLearningViewer';
import { useAccessibleBanks } from '../hooks/useApiQueries';
import { buildPlatformScope } from '@examflow/core';
import {
  formatExamSpaceIdentityLabel,
  resolveExamSpaceTheme,
} from '@examflow/learning-space';
import { cn } from '@examflow/ui';

type TrainingEntryController = ReturnType<typeof useTrainingEntryController>;

export function ExamTrainingEntryView({
  viewer,
  controller,
}: {
  viewer: UserLearningContext;
  controller: TrainingEntryController;
}) {
  const {
    model,
    selectedModule,
    selectedModuleId,
    selectedLevel,
    levelLabel,
    isOpenResponseSession,
    sessionSizeLabel,
    showWritingTaskPicker,
    selectedTaskNumber,
    taskLabel,
    trainingRecommendation,
    trainingRecommendationStatus,
    recentAttemptRecords,
    recentAttemptRecordsStatus,
    incompleteTraining,
    continueIncompleteTraining,
    discardIncompleteTraining,
    writingEvidenceSummary,
  } = controller;
  const contentIsReady = model.contentReadiness.status === 'ready';
  const contentChecking = model.contentReadiness.status === 'checking';
  /** Keep Skill chrome mounted while inventory access resolves — avoids checking→ready layout flash. */
  const showTrainingWorkspace = contentIsReady || contentChecking;
  const activeScope = buildPlatformScope({ examType: viewer.activeExamSpace.examCode });
  const theme = resolveExamSpaceTheme(viewer.activeExamSpace.examCode);
  const identityLabel = formatExamSpaceIdentityLabel(
    viewer.activeExamSpace.examCode,
    viewer.activeExamSpace.examName
  );

  return (
    <LearningShell viewer={viewer} activeNav="training" statusLabel="专项训练">
      <section data-testid="exam-space-training-hero">
        <p className="text-xs font-semibold tracking-[0.14em]" style={{ color: theme.accent }}>
          {identityLabel}
        </p>
        <h1
          className="mt-3 max-w-3xl font-black tracking-tight text-[#111827]"
          style={{ fontSize: 'clamp(2rem, 5vw, 3rem)', lineHeight: 1.08 }}
        >
          专项训练
        </h1>
        <p className="mt-4 max-w-2xl text-base leading-7 text-slate-600">
          {contentIsReady
            ? isOpenResponseSession
              ? `按真实考试练一篇：选官方 Task（词数与建议用时不同），写完 AI 改卷后可修订一稿。随时可停；正式评分会计入 ${viewer.activeExamSpace.examName} 能力诊断。`
              : `选一个科目，可选难度，直接开练。随时可停，已答题目会写回 ${viewer.activeExamSpace.examName} 能力诊断。`
            : model.contentReadiness.description}
        </p>
        <div className="mt-8 flex flex-wrap items-center gap-4">
          <ActionLink
            action={controller.startAction}
            onClick={controller.startActionHandler}
          />
          {controller.secondaryAction ? (
            <ActionLink
              action={controller.secondaryAction}
              onClick={controller.secondaryActionHandler}
            />
          ) : null}
          <ActionLink
            action={{
              id: 'back-workspace',
              label: '返回工作台',
              kind: 'ghost',
              href: buildScopedLearningHref('/workspace', activeScope),
            }}
          />
        </div>
      </section>

      {!contentIsReady && !contentChecking && (
        <div className="mt-10 rounded-md border border-white/70 bg-white/55 px-5 py-4">
          <ToneBadge tone={model.contentReadiness.tone}>
            内容未就绪
          </ToneBadge>
          <p className="mt-3 text-sm leading-6 text-slate-600">{model.contentReadiness.title}</p>
          <p className="mt-1 text-xs text-slate-500">{model.contentReadiness.scopeLabel}</p>
        </div>
      )}

      {contentChecking && (
        <div className="mt-6 rounded-md border border-white/70 bg-white/55 px-5 py-3 text-sm text-slate-600">
          正在确认可学内容…
        </div>
      )}

      {showTrainingWorkspace && (
        <>
          <section className="mt-10" data-testid="exam-space-training-modules">
            <div className="mb-4 flex items-baseline justify-between gap-4">
              <h2 className="text-[11px] font-bold tracking-[0.18em] text-slate-500">选科目</h2>
              <p className="text-[11px] text-slate-500">
                {selectedModule?.label}
                {sessionSizeLabel ? ` · ${sessionSizeLabel}` : ''}
                {taskLabel ? ` · ${taskLabel}` : ''}
                {levelLabel ? ` · ${levelLabel}` : ''}
              </p>
            </div>
            <div
              className={cn(
                'grid gap-3',
                model.moduleOptions.length >= 4
                  ? 'grid-cols-2 md:grid-cols-4'
                  : 'grid-cols-1 sm:grid-cols-3'
              )}
            >
              {model.moduleOptions.map((module) => {
                const selected = module.moduleId === selectedModuleId;
                return (
                  <button
                    key={module.moduleId}
                    type="button"
                    aria-pressed={selected}
                    disabled={module.disabled}
                    onClick={() => {
                      if (!module.disabled) controller.selectModule(module.moduleId);
                    }}
                    className={cn(
                      'min-h-[112px] rounded-md border bg-white/70 p-4 text-left transition-shadow',
                      module.disabled && 'cursor-not-allowed opacity-60',
                      !module.disabled && 'hover:shadow-md',
                      selected ? 'border-transparent' : 'border-white/60'
                    )}
                    style={
                      selected
                        ? {
                            boxShadow: `inset 3px 0 0 ${theme.accent}, 0 1px 2px rgba(28,42,34,0.06)`,
                            background: `linear-gradient(145deg, ${theme.accent}12 0%, rgba(255,255,255,0.85) 55%)`,
                          }
                        : undefined
                    }
                  >
                    <div className="flex items-start justify-between gap-2">
                      <ModuleIcon moduleId={module.moduleId} />
                      {selected ? (
                        <ToneBadge tone="current">已选择</ToneBadge>
                      ) : module.disabled ? (
                        <ToneBadge tone="locked">
                          {module.disabledReason?.includes('开放作答') ? '即将开放' : '待内容开放'}
                        </ToneBadge>
                      ) : module.recommended ? (
                        <ToneBadge tone="neutral">推荐</ToneBadge>
                      ) : null}
                    </div>
                    <div className="mt-3 text-base font-black tracking-tight text-[#111827]">
                      {module.label}
                    </div>
                    <div className="mt-2 text-xs text-slate-500">
                      {module.disabled
                        ? (module.disabledReason ?? '暂不可练')
                        : selected
                          ? `${sessionSizeLabel} · ${levelLabel}`
                          : module.questionCount === 1
                            ? '单任务 · 随时可停'
                            : '随时开练 · 随时可停'}
                    </div>
                  </button>
                );
              })}
            </div>
          </section>

          {showWritingTaskPicker ? (
            <section className="mt-8" data-testid="exam-space-training-writing-task">
              <h2 className="mb-3 text-[11px] font-bold tracking-[0.18em] text-slate-500">
                写作任务
              </h2>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  aria-pressed={selectedTaskNumber == null}
                  onClick={() => controller.selectTaskNumber(null)}
                  className={cn(
                    'rounded-md border px-4 py-2.5 text-left text-sm transition-colors',
                    selectedTaskNumber == null
                      ? 'border-transparent font-semibold text-[#111827]'
                      : 'border-white/70 bg-white/50 text-slate-600 hover:border-slate-200'
                  )}
                  style={
                    selectedTaskNumber == null
                      ? {
                          backgroundColor: theme.accentSoft,
                          color: theme.accent,
                          boxShadow: `inset 0 0 0 1px ${theme.accent}33`,
                        }
                      : undefined
                  }
                >
                  <span className="block font-bold">不限定</span>
                  <span className="mt-0.5 block text-xs opacity-80">库存中任一任务</span>
                </button>
                {controller.writingTaskOptions.map((option) => {
                  const selected = option.value === selectedTaskNumber;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      aria-pressed={selected}
                      onClick={() => controller.selectTaskNumber(option.value)}
                      className={cn(
                        'rounded-md border px-4 py-2.5 text-left text-sm transition-colors',
                        selected
                          ? 'border-transparent font-semibold text-[#111827]'
                          : 'border-white/70 bg-white/50 text-slate-600 hover:border-slate-200'
                      )}
                      style={
                        selected
                          ? {
                              backgroundColor: theme.accentSoft,
                              color: theme.accent,
                              boxShadow: `inset 0 0 0 1px ${theme.accent}33`,
                            }
                          : undefined
                      }
                    >
                      <span className="block font-bold">{option.label}</span>
                      <span className="mt-0.5 block text-xs opacity-80">
                        {[
                          option.hint,
                          option.suggestedMinutes != null
                            ? `建议 ${option.suggestedMinutes} 分钟`
                            : null,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    </button>
                  );
                })}
              </div>
            </section>
          ) : null}

          <section className="mt-8" data-testid="exam-space-training-level">
            <h2 className="mb-3 text-[11px] font-bold tracking-[0.18em] text-slate-500">难度</h2>
            <div className="flex flex-wrap gap-2">
              {controller.levelOptions.map((option) => {
                const selected = option.value === selectedLevel;
                return (
                  <button
                    key={option.value || 'any'}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => controller.selectLevel(option.value)}
                    className={cn(
                      'rounded-md border px-4 py-2.5 text-left text-sm transition-colors',
                      selected
                        ? 'border-transparent font-semibold text-[#111827]'
                        : 'border-white/70 bg-white/50 text-slate-600 hover:border-slate-200'
                    )}
                    style={
                      selected
                        ? {
                            backgroundColor: theme.accentSoft,
                            color: theme.accent,
                            boxShadow: `inset 0 0 0 1px ${theme.accent}33`,
                          }
                        : undefined
                    }
                  >
                    <span className="block font-bold">{option.label}</span>
                  </button>
                );
              })}
            </div>
          </section>

          <section className="mt-12 space-y-3" data-testid="exam-space-training-secondary">
            {selectedModuleId === 'writing' ? (
              <details className="rounded-md border border-white/70 bg-white/50 px-4 py-3">
                <summary className="cursor-pointer list-none text-sm font-semibold text-slate-700">
                  其他科目建议
                  {trainingRecommendationStatus === 'ready' &&
                  trainingRecommendation.targetModuleId !== 'writing' ? (
                    <span className="ml-2 font-normal text-slate-500">
                      · {trainingRecommendation.targetModuleLabel}
                    </span>
                  ) : null}
                </summary>
                <div className="mt-4">
                  {trainingRecommendationStatus === 'loading' ? (
                    <p className="text-sm text-slate-500">正在读取训练记录</p>
                  ) : trainingRecommendationStatus === 'unavailable' ? (
                    <p className="text-sm text-amber-700">
                      本地记录暂时不可用，先按当前科目开始训练。
                    </p>
                  ) : trainingRecommendation.targetModuleId === 'writing' ? (
                    <p className="text-sm leading-6 text-slate-600">
                      {trainingRecommendation.body}
                    </p>
                  ) : (
                    <>
                      <p className="text-sm leading-6 text-slate-600">
                        {trainingRecommendation.body}
                      </p>
                      <button
                        type="button"
                        onClick={() =>
                          controller.selectModule(trainingRecommendation.targetModuleId)
                        }
                        className="mt-4 text-sm font-semibold underline-offset-2 hover:underline"
                        style={{ color: theme.accent }}
                      >
                        改选{trainingRecommendation.targetModuleLabel}
                      </button>
                    </>
                  )}
                </div>
              </details>
            ) : (
              <details className="rounded-md border border-white/70 bg-white/50 px-4 py-3">
                <summary className="cursor-pointer list-none text-sm font-semibold text-slate-700">
                  训练建议
                  {trainingRecommendationStatus === 'ready' ? (
                    <span className="ml-2 font-normal text-slate-500">
                      · {trainingRecommendation.title}
                    </span>
                  ) : null}
                </summary>
                <div className="mt-4">
                  {trainingRecommendationStatus === 'loading' ? (
                    <p className="text-sm text-slate-500">正在读取训练记录</p>
                  ) : trainingRecommendationStatus === 'unavailable' ? (
                    <p className="text-sm text-amber-700">
                      本地记录暂时不可用，先按当前科目开始训练。
                    </p>
                  ) : (
                    <>
                      <ToneBadge tone={trainingRecommendation.tone}>
                        {trainingRecommendation.title}
                      </ToneBadge>
                      <p className="mt-3 text-sm leading-6 text-slate-600">
                        {trainingRecommendation.body}
                      </p>
                      <div className="mt-3 text-xs text-slate-500">
                        建议模块 {trainingRecommendation.targetModuleLabel}
                        <span className="mx-1.5">·</span>
                        {trainingRecommendation.prescriptionLabel}
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          controller.selectModule(trainingRecommendation.targetModuleId)
                        }
                        className="mt-4 text-sm font-semibold underline-offset-2 hover:underline"
                        style={{ color: theme.accent }}
                      >
                        {trainingRecommendation.actionLabel}
                      </button>
                    </>
                  )}
                </div>
              </details>
            )}

            <RecentTrainingRecordsPanel
              records={recentAttemptRecords}
              status={recentAttemptRecordsStatus}
              variant={selectedModuleId === 'writing' ? 'writing' : 'choice'}
              title={selectedModuleId === 'writing' ? '最近写作记录' : '最近训练记录'}
              evidenceNote={
                selectedModuleId === 'writing' && writingEvidenceSummary?.sampleCount
                  ? writingEvidenceSummary.focusReason
                  : null
              }
              subtitle={
                selectedModuleId === 'writing'
                  ? '展示 AI 评分与任务号；参考分不计入能力主证据。'
                  : incompleteTraining
                    ? '未交卷本地进度可继续；下方为已交卷表现。'
                    : '切换科目后，会显示对应科目的最近表现。'
              }
              emptyMessage={
                selectedModuleId === 'writing'
                  ? '当前还没有写作训练记录。'
                  : '当前科目还没有训练记录。'
              }
              incomplete={incompleteTraining}
              onContinueIncomplete={continueIncompleteTraining}
              onDiscardIncomplete={discardIncompleteTraining}
              className="border-white/70 bg-white/55"
            />
          </section>
        </>
      )}
    </LearningShell>
  );
}

export default function ExamTrainingEntryPage() {
  const [searchParams] = useSearchParams();
  const viewer = useLearningViewer(buildLearningRouteScope(searchParams));
  const activeScope = buildPlatformScope({ examType: viewer.activeExamSpace.examCode });
  const accessibleBanks = useAccessibleBanks(activeScope ?? undefined);
  const launchIntent = parseTrainingLaunchIntent(searchParams);
  const preferredModuleId = (launchIntent?.skillId ?? searchParams.get('moduleId')) as
    | TcfModuleId
    | null;
  const preferredLevel = searchParams.get('level');
  const preferredTaskRaw = searchParams.get('taskNumber');
  const preferredTaskNumber = preferredTaskRaw
    ? Number.parseInt(preferredTaskRaw, 10)
    : null;
  const controller = useTrainingEntryController(
    viewer,
    {
      accessibleBanks: accessibleBanks.data,
      accessibleBanksStatus: accessibleBanks.isLoading
        ? 'loading'
        : accessibleBanks.isError
          ? 'unavailable'
          : 'ready',
    },
    preferredModuleId,
    preferredLevel,
    Number.isFinite(preferredTaskNumber) ? preferredTaskNumber : null
  );

  return <ExamTrainingEntryView viewer={viewer} controller={controller} />;
}
